import { Box, Button, Divider, Grid, Typography } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import DeleteIcon from '@mui/icons-material/Delete'
import { useStyles } from './style'
import { useTenantFormContext } from '../tenant-form-context'
import { DomainInput } from './domain-input'
import { ActionButton } from '@emerald-works-nova/components'
import { BannerStatic } from '../../../components'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { useDispatch } from 'react-redux'
import { TenantEditSlice } from '../../../reducers'
import { SelfRegistrationToggle } from './toggle'
import { SelfRegistrationSuccessModal } from './modals/success-modal'
import { SelfRegistrationClearAllDomainsModal } from './modals/clear-all-modal'

export const selfRegistrationModalKeys = {
  success: 'self-registration-success-modal',
  disabled: 'self-registration-disabled-modal',
  clearAll: 'self-registration-clear-all-modal'
}
export const RegistrationDomainsTab = () => {
  const classes = useStyles()
  const { mainClasses, tenant } = useTenantFormContext()
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState(false)
  const [clearAllModal, setClearAllModal] = useState(false)
  const [successModal, setSuccessModal] = useState(false)

  const [modified, setModified] = useState(false)
  const [error, setError] = useState('')

  const getEmailDomainsFromTenant = useCallback((tenant) => {
    const provider = tenant.loginProviders.find(
      (lp) => lp.providerType === 'USERNAME'
    )

    return provider?.selfRegistrationDomains || []
  }, [])

  const emailDomains = useMemo(() => {
    return getEmailDomainsFromTenant(tenant)
  }, [getEmailDomainsFromTenant, tenant])

  const [domains, setDomains] = useState([])

  const showSuccessAlert = useCallback(
    (tenant) => {
      const value = localStorage.getItem(selfRegistrationModalKeys.success)
      const emailDomains = getEmailDomainsFromTenant(tenant)

      if (value !== 'no' && emailDomains.length > 0) {
        setSuccessModal(true)
      } else {
        BannerStatic.show({
          label: 'Self registration email domains updated successfully.'
        })
      }
    },
    [getEmailDomainsFromTenant]
  )

  const [tenantSelfRegistrationDomainUpdateEvent] = useEvent([
    {
      eventName: 'tenantSelfRegistrationDomainUpdate',
      onSuccess: (tenant) => {
        setIsLoading(false)
        setModified(false)
        dispatch(
          TenantEditSlice.actions.updateTenantLoginProviders(
            tenant.loginProviders
          )
        )

        showSuccessAlert(tenant)
      },
      onError: (error) => {
        const message =
          error?.code === 'VALIDATION_ERROR' && error?.description
            ? error.description
            : 'An error occurred while updating the self registration email domains.'

        console.log('on error', error)

        BannerStatic.show({
          label: message,
          isError: true
        })
        setError(message)
        setIsLoading(false)
      }
    }
  ])

  const sorted = useMemo(() => {
    const sorted = [...domains]
    sorted.sort((a, b) => a.localeCompare(b))

    return sorted
  }, [domains])

  const validateDomains = useCallback((domains) => {
    const regex = /^(?!-)[a-zA-Z0-9-]{1,63}(?<!-)(\.[a-zA-Z0-9-]{1,63}){1,}$/

    for (const domain of domains) {
      const valid = regex.test(domain)

      if (!valid) {
        return `${domain} is not a valid email domain.`
      }
    }

    return ''
  }, [])

  const handleSave = async () => {
    setError('')
    setIsLoading(true)

    const error = validateDomains(domains)

    if (error) {
      setError(error)
      setIsLoading(false)
      return
    }

    await tenantSelfRegistrationDomainUpdateEvent.trigger({
      tenantKey: tenant.pk,
      domains: domains
    })
  }

  const handleSubmit = async () => {
    const value = localStorage.getItem(selfRegistrationModalKeys.clearAll)

    if (domains.length === 0 && value !== 'no') {
      // Show modal for submitting with no domains.
      setClearAllModal(true)
    } else {
      // Submit domains.
      await handleSave()
    }
  }
  const handleModified = () => {
    setModified(true)
  }

  const clearAll = () => {
    if (domains.length > 0) {
      setDomains([])
      handleModified()
    }
  }

  useEffect(() => {
    setDomains(emailDomains)
  }, [emailDomains])

  return (
    <Box className={classes.root}>
      <SelfRegistrationSuccessModal
        open={successModal}
        handleClose={() => setSuccessModal(false)}
        tenant={tenant}
        id={selfRegistrationModalKeys.success}
      />

      <SelfRegistrationClearAllDomainsModal
        open={clearAllModal}
        handleProceed={handleSave}
        handleClose={() => setClearAllModal(false)}
        id={selfRegistrationModalKeys.clearAll}
      />

      <Grid container alignItems='center' className={mainClasses.ctnTitle}>
        <Grid item xs={12}>
          <Typography variant='h6'>
            Domain-Restricted Self-Registration
          </Typography>
          <SelfRegistrationToggle tenant={tenant} useModal />
        </Grid>
      </Grid>
      <Grid container spacing={4} marginY={1}>
        <Grid item lg={5} xs={12}>
          <Box>
            <Typography className={classes.description}>
              Enable self-registration with ease by managing a whitelist of
              approved domains, granting users the ability to register using
              only specified email domains.
            </Typography>
          </Box>
          <Divider className={classes.divider} />
          <Box>
            <Typography className={classes.description}>
              Enter a domain (e.g., example.com) and press "Enter" or separate
              multiple domains with commas.
            </Typography>
            <ul>
              <li>e.g. example.com, my-domain.org, company.net</li>
            </ul>

            <Typography className={classes.description}>
              Bulk Domain Upload
            </Typography>
            <ul>
              <li>
                For multiple domains, copy and paste your list of domains into
                the allowed field on the left side, separating each domain with
                a comma.
              </li>
            </ul>
          </Box>
        </Grid>
        <Grid item lg={7} xs={12}>
          <Box>
            <DomainInput
              id='self-registration-domains'
              domains={sorted}
              onEdit={handleModified}
              label='Allowed domains'
              setDomains={setDomains}
              placeholder='Enter domain'
            />
            {error && (
              <Typography color='error' marginY={2}>
                {error}
              </Typography>
            )}
          </Box>

          <Box className={classes.btnSection}>
            <Button
              data-test='clear-all-domains'
              size='small'
              startIcon={<DeleteIcon />}
              className={classes.clearBtn}
              onClick={() => clearAll()}
            >
              Clear all
            </Button>
            <ActionButton
              data-test='save-domains'
              onClick={handleSubmit}
              disabled={!modified || isLoading}
              variant='primary'
              style={{ height: 'min-content' }}
            >
              {isLoading ? 'Please wait' : 'Save Domains'}
            </ActionButton>
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}
